import { ReactNode } from 'react'
import { Dialog, DialogHeader, DialogContent, DialogFooter } from './Dialog'
import { Button } from './button'
import {
  CheckCircle,
  Warning,
  XCircle,
  Info,
} from '@phosphor-icons/react'

type AlertVariant = 'success' | 'warning' | 'error' | 'info'

interface AlertDialogProps {
  isOpen: boolean
  onClose: () => void
  title: string
  message: string | ReactNode
  buttonText?: string
  variant?: AlertVariant
  icon?: ReactNode
  onButtonClick?: () => void
}

const variantConfig = {
  success: {
    icon: <CheckCircle size={40} weight="duotone" />,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    buttonColor: 'bg-emerald-600 hover:bg-emerald-700',
  },
  warning: {
    icon: <Warning size={40} weight="duotone" />,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    buttonColor: 'bg-amber-600 hover:bg-amber-700',
  },
  error: {
    icon: <XCircle size={40} weight="duotone" />,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    buttonColor: 'bg-red-600 hover:bg-red-700',
  },
  info: {
    icon: <Info size={40} weight="duotone" />,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    buttonColor: 'bg-blue-600 hover:bg-blue-700',
  },
}

export function AlertDialog({
  isOpen,
  onClose,
  title,
  message,
  buttonText = 'OK',
  variant = 'info',
  icon,
  onButtonClick,
}: AlertDialogProps) {
  const config = variantConfig[variant]

  const handleClick = () => {
    if (onButtonClick) {
      onButtonClick()
    }
    onClose()
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="sm"
      showCloseButton={false}
    >
      <DialogHeader>
        <div className="flex flex-col items-center text-center">
          <div className={`w-20 h-20 rounded-full ${config.iconBg} ${config.iconColor} flex items-center justify-center mb-4`}>
            {icon || config.icon}
          </div>
          <h3 className="text-xl font-semibold text-gray-900">
            {title}
          </h3>
        </div>
      </DialogHeader>

      <DialogContent>
        <div className="text-center text-gray-600">
          {typeof message === 'string' ? <p>{message}</p> : message}
        </div>
      </DialogContent>

      <DialogFooter className="justify-center">
        <Button
          onClick={handleClick}
          className={`text-white min-w-[120px] ${config.buttonColor}`}
        >
          {buttonText}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

// Convenience components

interface SuccessDialogProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  message: string
  buttonText?: string
  onButtonClick?: () => void
}

export function SuccessDialog({
  isOpen,
  onClose,
  title = 'Sucesso!',
  message,
  buttonText = 'OK',
  onButtonClick,
}: SuccessDialogProps) {
  return (
    <AlertDialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      message={message}
      buttonText={buttonText}
      variant="success"
      onButtonClick={onButtonClick}
    />
  )
}

interface ErrorDialogProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  message: string
  buttonText?: string
}

export function ErrorDialog({
  isOpen,
  onClose,
  title = 'Erro',
  message,
  buttonText = 'OK',
}: ErrorDialogProps) {
  return (
    <AlertDialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      message={message}
      buttonText={buttonText}
      variant="error"
    />
  )
}

interface WarningDialogProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  message: string
  buttonText?: string
}

export function WarningDialog({
  isOpen,
  onClose,
  title = 'Atencao',
  message,
  buttonText = 'Entendi',
}: WarningDialogProps) {
  return (
    <AlertDialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      message={message}
      buttonText={buttonText}
      variant="warning"
    />
  )
}

interface InfoDialogProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  message: string
  buttonText?: string
}

export function InfoDialog({
  isOpen,
  onClose,
  title = 'Informacao',
  message,
  buttonText = 'OK',
}: InfoDialogProps) {
  return (
    <AlertDialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      message={message}
      buttonText={buttonText}
      variant="info"
    />
  )
}
